/**
 * Custom Dialog System for Wishlist
 * Replaces native alert(), confirm(), and prompt() with branded modals
 */

class WishlistDialogs {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready before creating container
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.createContainer());
    } else {
      this.createContainer();
    }
  }

  createContainer() {
    // Create dialog container if it doesn't exist
    if (!document.getElementById('wishlist-dialogs-container')) {
      const container = document.createElement('div');
      container.id = 'wishlist-dialogs-container';
      document.body.appendChild(container);
    }
  }

  /**
   * Show custom alert dialog
   */
  alert(message, title = 'Notice') {
    return new Promise((resolve) => {
      const dialog = this.createDialog({
        title,
        message,
        type: 'alert',
        buttons: [
          { text: 'OK', action: 'primary', handler: () => resolve(true) }
        ]
      });
      this.showDialog(dialog);
    });
  }

  /**
   * Show custom confirm dialog
   */
  confirm(message, title = 'Confirm') {
    return new Promise((resolve) => {
      const dialog = this.createDialog({
        title,
        message,
        type: 'confirm',
        buttons: [
          { text: 'Cancel', action: 'secondary', handler: () => resolve(false) },
          { text: 'OK', action: 'primary', handler: () => resolve(true) }
        ]
      });
      this.showDialog(dialog);
    });
  }

  /**
   * Show custom prompt dialog
   */
  prompt(message, defaultValue = '', title = 'Enter') {
    return new Promise((resolve) => {
      const dialog = this.createDialog({
        title,
        message,
        type: 'prompt',
        defaultValue,
        buttons: [
          { text: 'Cancel', action: 'secondary', handler: () => resolve(null) },
          { text: 'OK', action: 'primary', handler: () => {
            const input = dialog.querySelector('[data-dialog-input]');
            resolve(input ? input.value.trim() : null);
          }}
        ]
      });
      this.showDialog(dialog);
    });
  }

  /**
   * Create dialog element
   */
  createDialog({ title, message, type, defaultValue = '', buttons }) {
    const container = document.getElementById('wishlist-dialogs-container');
    
    const overlay = document.createElement('div');
    overlay.className = 'wishlist-dialog-overlay';
    
    const dialog = document.createElement('div');
    dialog.className = 'wishlist-dialog';
    
    let inputHTML = '';
    if (type === 'prompt') {
      inputHTML = `
        <div class="wishlist-dialog__field">
          <input 
            type="text" 
            class="wishlist-dialog__input" 
            data-dialog-input
            value="${this.escapeHtml(defaultValue)}"
            placeholder="${this.escapeHtml(message)}"
            autofocus
          />
        </div>
      `;
    }
    
    dialog.innerHTML = `
      <div class="wishlist-dialog__content">
        <h3 class="wishlist-dialog__title">${this.escapeHtml(title)}</h3>
        ${type !== 'prompt' ? `<p class="wishlist-dialog__message">${this.escapeHtml(message)}</p>` : ''}
        ${inputHTML}
        <div class="wishlist-dialog__actions">
          ${buttons.map((btn, index) => `
            <button 
              class="wishlist-dialog__button wishlist-dialog__button--${btn.action}"
              data-dialog-button="${index}"
              type="button"
            >
              ${this.escapeHtml(btn.text)}
            </button>
          `).join('')}
        </div>
      </div>
    `;
    
    // Add button handlers
    buttons.forEach((btn, index) => {
      const button = dialog.querySelector(`[data-dialog-button="${index}"]`);
      if (button) {
        button.addEventListener('click', () => {
          btn.handler();
          this.closeDialog(overlay);
        });
      }
    });
    
    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        // For confirm/prompt, cancel on overlay click
        if (type === 'confirm' || type === 'prompt') {
          buttons.find(b => b.action === 'secondary')?.handler();
          this.closeDialog(overlay);
        }
      }
    });
    
    // Handle Enter key for prompt
    if (type === 'prompt') {
      const input = dialog.querySelector('[data-dialog-input]');
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            buttons.find(b => b.action === 'primary')?.handler();
            this.closeDialog(overlay);
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            buttons.find(b => b.action === 'secondary')?.handler();
            this.closeDialog(overlay);
          }
        });
      }
    }
    
    overlay.appendChild(dialog);
    return overlay;
  }

  /**
   * Show dialog
   */
  showDialog(dialog) {
    const container = document.getElementById('wishlist-dialogs-container');
    container.appendChild(dialog);
    
    // Trigger animation
    requestAnimationFrame(() => {
      dialog.classList.add('is-active');
    });
  }

  /**
   * Close dialog
   */
  closeDialog(dialog) {
    dialog.classList.remove('is-active');
    setTimeout(() => {
      if (dialog.parentNode) {
        dialog.parentNode.removeChild(dialog);
      }
    }, 300);
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.WishlistDialogs = new WishlistDialogs();
  });
} else {
  // DOM already ready
  window.WishlistDialogs = new WishlistDialogs();
}

// Replace native dialogs globally (optional - can be scoped to wishlist code)
window.wishlistAlert = (message, title) => {
  if (window.WishlistDialogs) {
    return window.WishlistDialogs.alert(message, title);
  }
  return Promise.resolve(alert(message));
};

window.wishlistConfirm = (message, title) => {
  if (window.WishlistDialogs) {
    return window.WishlistDialogs.confirm(message, title);
  }
  return Promise.resolve(confirm(message));
};

window.wishlistPrompt = (message, defaultValue, title) => {
  if (window.WishlistDialogs) {
    return window.WishlistDialogs.prompt(message, defaultValue, title);
  }
  return Promise.resolve(prompt(message, defaultValue));
};
